/* eslint-disable max-classes-per-file */

import { sortBy } from "lodash";
import { Observable, of } from "rxjs";
import { TransporttilldelaHandelssortimentCommand } from "../../../models/commands";
import { ITplHandelssortiment } from "../../../models/dtos";
import { IBefraktare, ITransportforetag } from "../../../models/queries";

export enum HandelssortimentDisplayMode
{
    none = "none",
    deviating = "deviating",
    all = "all"
}

export interface ITilldelningOption {
    key: string;
    value: string;

    bind(command: TransporttilldelaHandelssortimentCommand): void;
    match(defaultTransportforetagId: number, handelssortiment: ITplHandelssortiment): boolean;

}

export class TilldelasEjOption implements ITilldelningOption {
    key: string;
    value: string;

    constructor() {
        this.key = "tde";
        this.value = "Transporttilldelas ej";
    }

    bind(command: TransporttilldelaHandelssortimentCommand) {
        command.egenTransport = false;
        command.transportforetagId = null;
        command.befraktareId = null;
    }

    match(defaultTransportforetagId: number, handelssortiment: ITplHandelssortiment): boolean {
        return handelssortiment.egenTransport === false;
    }
}

export class TransportforetagOption implements ITilldelningOption {
    key: string;
    value: string;

    constructor(private readonly raw: ITransportforetag) {
        this.key = `transportforetag/${raw.id}`;
        this.value = `${raw.huvudkod} ${raw.namn}`;
    }

    bind(command: TransporttilldelaHandelssortimentCommand) {
        command.egenTransport = true;
        command.transportforetagId = this.raw.id;
        command.befraktareId = null;
    }

    match(defaultTransportforetagId: number, handelssortiment: ITplHandelssortiment): boolean {
        return this.raw.id === (handelssortiment.transportforetagId ?? defaultTransportforetagId);
    }
}

export class BefraktareOption implements ITilldelningOption {
    key: string;
    value: string;

    constructor(private readonly raw: IBefraktare) {
        this.key = `befraktare/${raw.id}`;
        const befraktareLabel = raw.befraktare ? "Befraktare" : "fd Befraktare";
        this.value = `${raw.huvudkod} ${raw.namn} (${befraktareLabel})`;
    }

    bind(command: TransporttilldelaHandelssortimentCommand) {
        command.egenTransport = true;
        command.transportforetagId = null;
        command.befraktareId = this.raw.id;
    }

    match(defaultTransportforetagId: number, handelssortiment: ITplHandelssortiment): boolean {
        if (handelssortiment.egenTransport === false) {
            return false;
        }
        if (handelssortiment.transportforetagId) {
            return false;
        }
        return this.raw.id === handelssortiment.befraktareId;
    }
}

export class TransporttilldelningOptions {
    values: ITilldelningOption[];
    values$: Observable<ITilldelningOption[]>;

    constructor(befraktare: IBefraktare[], transportforetag: ITransportforetag[]) {
        this.values = this.buildOptions(befraktare, transportforetag);
        this.values$ = of(this.values);
    }

    getOptionKey(handelssortiment: ITplHandelssortiment): string {
        return this.values.find(option => option.match(null, handelssortiment))?.key;
    }

    get(key: string): ITilldelningOption {
        return this.values.find(x => x.key === key);
    }

    getMany(keys: string[]): ITilldelningOption[] {
        return this.values.filter(x => keys.includes(x.key));
    }

    private buildOptions(befraktare: IBefraktare[], transportforetag: ITransportforetag[]): ITilldelningOption[] {
        const befraktareOptions = sortBy(befraktare.map(x => new BefraktareOption(x)), x => x.value);
        const transportforetagOptions = sortBy(transportforetag.map(x => new TransportforetagOption(x)), x => x.value);

        return [
            new TilldelasEjOption(),
            ...transportforetagOptions,
            ...befraktareOptions
        ];
    }
}
